import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  athletesQuery,
  enrollmentsQuery,
  formatPrice,
  programsQuery,
  useStartCheckout,
  type Athlete,
  type Enrollment,
  type Program,
} from "@/lib/family";
import type { PaymentMethod } from "@/lib/fees";
import { gradeLabel, gradeRangeLabel, isGradeInRange } from "@/lib/grades";

export const Route = createFileRoute("/_app/home")({
  component: FamilyHome,
});

function FamilyHome() {
  const { profile } = Route.useRouteContext();
  const athletes = useQuery(athletesQuery);
  const programs = useQuery(programsQuery);
  const enrollments = useQuery(enrollmentsQuery);

  return (
    <div className="space-y-10">
      <section>
        <h1 className="font-display text-4xl font-extrabold uppercase italic">
          Welcome{profile.first_name ? `, ${profile.first_name}` : ""}.
        </h1>
        <p className="mt-2 text-white/70">
          Manage your players and Winter Season registration.{" "}
          <Link to="/waiver" className="underline decoration-white/30 underline-offset-4 hover:text-white">
            Registration agreement
          </Link>
        </p>
      </section>

      <section>
        <div className="flex items-center justify-between gap-4">
          <h2 className="font-display text-2xl font-extrabold uppercase italic">Your players</h2>
          <Link to="/players/new" className="rounded-md border border-white/20 px-3 py-1.5 text-sm font-semibold hover:border-white/40">
            + Add player
          </Link>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {athletes.isLoading && <p className="text-sm text-white/60">Loading…</p>}
          {athletes.data?.length === 0 && (
            <p className="rounded-md border border-dashed border-white/15 p-5 text-sm text-white/60 sm:col-span-2">
              No players yet. Add your daughter to register her for the season.
            </p>
          )}
          {athletes.data?.map((a) => <PlayerCard key={a.id} athlete={a} />)}
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl font-extrabold uppercase italic">Winter Season</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {programs.data?.map((p) => (
            <ProgramCard
              key={p.id}
              program={p}
              athletes={athletes.data ?? []}
              enrollments={new Map((enrollments.data ?? []).filter((e) => e.program_id === p.id).map((e) => [e.athlete_id, e]))}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function PlayerCard({ athlete }: { athlete: Athlete }) {
  const name = athlete.preferred_name || athlete.first_name;
  return (
    <Link
      to="/players/$athleteId"
      params={{ athleteId: athlete.id }}
      className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.03] p-4 hover:border-white/25"
    >
      <div>
        <p className="font-semibold">
          {name} {athlete.last_name}
        </p>
        <p className="text-sm text-white/60">{gradeLabel(athlete.grade)}</p>
      </div>
      <span className="text-xs text-white/50">Edit</span>
    </Link>
  );
}

function ProgramCard({
  program,
  athletes,
  enrollments,
}: {
  program: Program;
  athletes: Athlete[];
  enrollments: Map<string, Enrollment>;
}) {
  const eligible = athletes.filter((a) => isGradeInRange(a.grade, program.grade_min, program.grade_max));
  return (
    <article className="rounded-md border border-white/10 bg-white/[0.03] p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-a1-red">{program.season}</p>
      <h3 className="mt-1 text-lg font-semibold">{program.name}</h3>
      {program.short_description && <p className="mt-1 text-sm text-white/70">{program.short_description}</p>}
      <p className="mt-3 font-display text-3xl font-extrabold italic">{formatPrice(program.price_cents)}</p>
      <p className="text-xs text-white/50">{gradeRangeLabel(program.grade_min, program.grade_max)} · processing fee included</p>
      <div className="mt-4 border-t border-white/10 pt-3 text-sm">
        {!program.is_active ? (
          <p className="text-white/70">Registration opens soon. We'll email you when payments are ready.</p>
        ) : eligible.length === 0 ? (
          <p className="text-white/60">Add a player in this grade range to register.</p>
        ) : (
          <ul className="divide-y divide-white/10">
            {eligible.map((a) => (
              <PlayerRegistration key={a.id} program={program} athlete={a} enrollment={enrollments.get(a.id)} />
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}

function PlayerRegistration({ program, athlete, enrollment }: { program: Program; athlete: Athlete; enrollment?: Enrollment }) {
  const [choosing, setChoosing] = useState(false);
  const checkout = useStartCheckout();
  const name = athlete.preferred_name || athlete.first_name;

  function pay(method: PaymentMethod) {
    checkout.mutate({ programId: program.id, athleteId: athlete.id, method });
  }

  let status: React.ReactNode = null;
  if (enrollment?.status === "paid") status = <span className="font-semibold text-emerald-400">Registered ✓</span>;
  else if (enrollment?.payment_submitted_at) status = <span className="text-amber-300">Bank payment processing</span>;

  return (
    <li className="py-2.5">
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium">{name}</span>
        {status ?? (
          !choosing && (
            <button
              type="button"
              onClick={() => setChoosing(true)}
              className="rounded-md bg-a1-red px-3 py-1.5 text-sm font-semibold text-white hover:bg-a1-red/90"
            >
              Register &amp; pay
            </button>
          )
        )}
      </div>
      {!status && enrollment?.payment_error && <p className="mt-1 text-xs text-red-400">{enrollment.payment_error}</p>}
      {!status && choosing && (
        <div className="mt-2 rounded-md border border-white/10 bg-white/[0.03] p-3">
          <p className="text-xs text-white/60">How would you like to pay {formatPrice(program.price_cents)}?</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              disabled={checkout.isPending}
              onClick={() => pay("card")}
              className="rounded-md bg-a1-red px-3 py-2 text-sm font-semibold text-white hover:bg-a1-red/90 disabled:opacity-60"
            >
              Card
            </button>
            <button
              type="button"
              disabled={checkout.isPending}
              onClick={() => pay("ach")}
              className="rounded-md border border-white/20 px-3 py-2 text-sm font-semibold hover:border-white/40 disabled:opacity-60"
            >
              Bank account (ACH)
            </button>
          </div>
          <p className="mt-2 text-xs text-white/50">
            {checkout.isPending ? "Opening secure checkout…" : "You'll finish on Stripe's secure checkout page."}
          </p>
          {checkout.error && <p className="mt-1 text-xs text-red-400">{checkout.error.message}</p>}
          <button type="button" onClick={() => setChoosing(false)} className="mt-1 text-xs text-white/50 hover:text-white">
            Cancel
          </button>
        </div>
      )}
    </li>
  );
}
